import { useState } from 'react';
import { Link } from 'react-router-dom';
import { invitationApi, type Scope } from '../../api/memberships';

type Row = { email: string; scopeType: Scope['type']; message: string };

function currentScope(type: Scope['type']): Scope {
  const tenant = localStorage.getItem('aiecom_tenant_id') ?? import.meta.env.VITE_TENANT_ID ?? '';
  const org = localStorage.getItem('aiecom_org_id') ?? import.meta.env.VITE_ORG_ID ?? '';
  const workspace = localStorage.getItem('aiecom_workspace_id') ?? import.meta.env.VITE_WORKSPACE_ID ?? 'ws-a';
  return {
    type,
    tenant_id: tenant,
    organization_id: type !== 'tenant' ? org : undefined,
    workspace_id: type === 'workspace' ? workspace : undefined,
  };
}

export function BulkInvitationPage() {
  const [rows, setRows] = useState<Row[]>([{ email: '', scopeType: 'workspace', message: '' }]);
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  function updateRow(i: number, patch: Partial<Row>) {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResult('');
    try {
      const invitations = rows
        .filter((r) => r.email.trim())
        .map((r) => ({
          email: r.email.trim(),
          scope: currentScope(r.scopeType),
          message: r.message || undefined,
        }));
      const res = await invitationApi.bulk(invitations);
      setResult(`Sent ${res.created.length} invitation(s)${res.failed?.length ? `, ${res.failed.length} failed` : ''}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bulk invite failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl p-6">
      <Link to="/users/invitations" className="text-sm text-slate-600 hover:underline">← Invitations</Link>
      <h1 className="mt-4 text-2xl font-semibold">Bulk Invitation Wizard</h1>
      <p className="mt-1 text-sm text-slate-600">Invite multiple members in one batch.</p>
      <form onSubmit={(e) => void submit(e)} className="mt-6 space-y-4">
        {rows.map((row, i) => (
          <div key={i} className="rounded-xl border bg-white p-4">
            <input
              value={row.email}
              onChange={(e) => updateRow(i, { email: e.target.value })}
              placeholder="Email address"
              className="w-full rounded-lg border px-3 py-2 text-sm"
              type="email"
            />
            <select value={row.scopeType} onChange={(e) => updateRow(i, { scopeType: e.target.value as Scope['type'] })} className="mt-2 w-full rounded-lg border px-3 py-2 text-sm">
              <option value="tenant">Tenant</option>
              <option value="organization">Organization</option>
              <option value="workspace">Workspace</option>
            </select>
            <input
              value={row.message}
              onChange={(e) => updateRow(i, { message: e.target.value })}
              placeholder="Optional message"
              className="mt-2 w-full rounded-lg border px-3 py-2 text-sm"
            />
          </div>
        ))}
        <button type="button" onClick={() => setRows((r) => [...r, { email: '', scopeType: 'workspace', message: '' }])} className="text-sm text-slate-700 hover:underline">
          + Add another
        </button>
        <div>
          <button type="submit" disabled={loading} className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white disabled:opacity-50">
            {loading ? 'Sending…' : 'Send invitations'}
          </button>
        </div>
      </form>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      {result && <p className="mt-4 text-sm text-green-700">{result}</p>}
    </div>
  );
}
